/**
 * Grid  component props
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BP, TSchemaConstObjectPropsSpec } from "@adapptio/lib-blueprint";

import {
	getValuesFromStringEnum,
	LoadingSchema,
	propGroups,
	SPACING,
	spacingPattern,
	SPACING_default,
	SPACING_none,
	termsEditor as LibComponentsTerms
} from "@adapptio/lib-components";
import { GRID_ROLE, GRID_ROLE_default } from "../../Enums/GRID_ROLE";

import { termsEditor } from "../../terms";

/**
 * Grid Item Inherited props
 */
export const GridItemInheritedProps = {
	gridItemPosition: BP.Prop(
		BP.ResponsiveValue({
			...termsEditor.schemas.grid.contentItemPosition,
			value: BP.Object({
				props: {
					x: BP.Prop(
						BP.Integer({
							...termsEditor.schemas.grid.contentItemPositionX,
							constraints: {
								min: 0
							}
						}),
						10
					),
					y: BP.Prop(
						BP.Integer({
							...termsEditor.schemas.grid.contentItemPositionY,
							constraints: {
								min: 0
							}
						}),
						20
					),
					width: BP.Prop(
						BP.Integer({
							...termsEditor.schemas.grid.contentItemPositionWidth,
							constraints: {
								min: 1
							}
						}),
						30
					),
					height: BP.Prop(
						BP.Integer({
							...termsEditor.schemas.grid.contentItemPositionHeight,
							constraints: {
								min: 1
							}
						}),
						40
					)
				}
			})
		}),
		10
	),

	gridItemSpacing: BP.Prop(
		BP.StringWithConst({
			...termsEditor.schemas.grid.contentItemSpacing,
			constants: getValuesFromStringEnum(SPACING, LibComponentsTerms.schemas.common.spacingValues),
			default: "", // Has to stay empty
			constraints: {
				...LibComponentsTerms.schemas.common.spacingConstraints,
				pattern: spacingPattern
			}
		}),
		20
	)
};

/**
 * Grid Item Inherited props type
 */
export type TGridItemInheritedProps = typeof GridItemInheritedProps;

/**
 * Grid props
 */
export const ComponentGrid_Props = {
	content: BP.Prop(
		BP.ComponentList({
			...termsEditor.schemas.grid.content,
			alias: "gridContent",
			inheritedProps: {
				...GridItemInheritedProps
			}
		}),
		0,
		propGroups.common
	),

	columns: BP.Prop(
		BP.Integer({
			...termsEditor.schemas.grid.columns,
			default: 12,
			constraints: {
				min: 1,
				max: 24,
				required: true
			}
		}),
		100,
		propGroups.common
	),

	padding: BP.Prop(
		BP.StringWithConst({
			...termsEditor.schemas.grid.padding,
			constants: getValuesFromStringEnum(SPACING, LibComponentsTerms.schemas.common.spacingValues),
			default: SPACING_none,
			constraints: {
				...LibComponentsTerms.schemas.common.spacingConstraints,
				pattern: spacingPattern
			}
		}),
		100,
		propGroups.style
	),

	spacing: BP.Prop(
		BP.StringWithConst({
			...termsEditor.schemas.grid.spacing,
			constants: getValuesFromStringEnum(SPACING, LibComponentsTerms.schemas.common.spacingValues),
			default: SPACING_default,
			constraints: {
				...LibComponentsTerms.schemas.common.spacingConstraints,
				pattern: spacingPattern
			}
		}),
		110,
		propGroups.style
	),

	loading: BP.Prop(LoadingSchema(), 100, propGroups.advanced),

	role: BP.Prop(
		BP.Enum.String({
			...termsEditor.schemas.grid.role,
			options: getValuesFromStringEnum(GRID_ROLE, termsEditor.schemas.grid.roleValues),
			default: GRID_ROLE_default,
			fallbackValue: GRID_ROLE_default,
			constraints: {
				required: true
			}
		}),
		110,
		propGroups.advanced
	)
};

/**
 * Grid props type
 */
export type TComponentGrid_Props = Partial<TSchemaConstObjectPropsSpec<typeof ComponentGrid_Props>>;
